import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {Evaluator, Version} from "@cm/graph-v3"
import {CircularRef, versionChain, graphNode} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import type {DecalMaskType, ImageLikeNode, MaterialLikeNode, MeshLikeNode, NumberLike} from "#template/nodes/types"
import {decalMaskType} from "#template/nodes/validation"
import {imageLike} from "#template/nodes/image/fundamental"
import {meshLike} from "#template/nodes/object/mesh/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import {ObjectNode} from "#template/nodes/object/object-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import {MaterialAssignment} from "#template/nodes/material-assignment/material-assignment"
import {setupMaterialAssignmentWithOverride} from "#template/nodes/material-assignment/material-assignments"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import type {CmLogger} from "@cm/log"
import {SceneNodes} from "#template/interfaces/scene-object"
import {GetMeshDecal} from "#template/runtime-graph/nodes/get-mesh-decal"
import {TransformColorOverlay} from "#template/runtime-graph/nodes/transform-color-overlay"
import type {ImageGenerator, ImageNodeGenerator, IMaterialData, InputMaterialConnection, MaterialGraph, MeshRenderSettings} from "@cm/material-nodes"
import {BsdfPrincipled, Mapping, materialConnection, mul, RgbToBw, sub, UvMap} from "@cm/material-nodes"
import {isEvaluatedTemplateNode} from "#template/utils/template-node-utils"
import {visitNone} from "#template/template-node/visit-helpers"

type DecalMaskData = {
    maskImage?: ImageNodeGenerator
    colorOverlayImage?: ImageNodeGenerator
    widthCm: number
    heightCm: number
    invert: boolean
}

export type MeshDecalParameters = {
    name: string
    mesh: MeshLikeNode | null
    mask?: ImageLikeNode
    invertMask: boolean
    maskType: DecalMaskType
    color?: ImageLikeNode
    offset: [NumberLike, NumberLike]
    rotation: NumberLike
    size: [NumberLike | null, NumberLike | null]
    distance: NumberLike
    materialAssignment: MaterialAssignment | null
} & ObjectNodeParameters

type V0 = Omit<MeshDecalParameters, "materialAssignment"> & {material: MaterialLikeNode | null}

type V1 = MeshDecalParameters

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        const {material, ...rest} = parameters
        if (material instanceof CircularRef) throw new Error("Cannot resolve circular references when going from v0 to v1 in mesh-decal node")
        return {...rest, materialAssignment: material ? new MaterialAssignment({parameters: {node: material, side: "front"}}) : null}
    },
}

const meshDecalDefinition: ObjectNodeDefinition<MeshDecalParameters> = {
    nodeClass: "MeshDecal",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                mesh: z.nullable(meshLike),
                mask: z.optional(imageLike),
                invertMask: z.boolean(),
                maskType: decalMaskType,
                color: z.optional(imageLike),
                offset: z.tuple([numberLike, numberLike]),
                rotation: numberLike,
                size: z.tuple([z.nullable(numberLike), z.nullable(numberLike)]),
                distance: numberLike,
                materialAssignment: z.nullable(graphNode(MaterialAssignment)),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0]),
}

@registerTemplateGraphNode
export class MeshDecal extends ObjectNode<MeshDecalParameters> {
    public override get definition() {
        return meshDecalDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MeshDecalParameters> {
        if (this.parameters.mesh === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MeshDecalParameters> {
        const {
            mesh,
            materialAssignment,
            invertMask,
            mask,
            maskType,
            color,
            size: inputSizeParam,
            offset: offsetParam,
            rotation: rotationParam,
            distance: distanceParam,
        } = this.parameters

        if (!mesh) return skipped

        const {evaluator} = context
        const {templateContext, activeNodeSet} = evaluator
        const {sceneManager, computeDummyReifiedMeshData, logger} = templateContext

        const scope = evaluator.getScope(this)

        const [meshObjectData, meshObjectDataInvalid] = scope.branch(evaluator.evaluate(mesh))

        const maskImageData = evaluator.evaluate(mask ?? null)
        const colorImageData = evaluator.evaluate(color ?? null)

        const inputSize = scope.tuple(
            inputSizeParam[0] !== null ? evaluator.evaluate(inputSizeParam[0]) : null,
            inputSizeParam[1] !== null ? evaluator.evaluate(inputSizeParam[1]) : null,
        )

        const size = scope.pureLambda(
            // pure-lambda-allow: logger
            scope.tuple(
                inputSize,
                scope.pureLambda(
                    scope.tuple(maskImageData, colorImageData),
                    ([maskImageData, colorImageData]) => maskImageData ?? colorImageData,
                    "relevantDataObject",
                ),
            ),
            ([inputSize, imageData]) => computeConstrainedSizeFromImageData(inputSize, imageData, logger),
            "constrainedSize",
        )

        const [offsetValid, offsetInvalid] = scope.branch(
            scope.pureLambda(
                scope.tuple(evaluator.evaluate(offsetParam[0]), evaluator.evaluate(offsetParam[1])),
                (offset): [number, number] | null => (offset[0] === null || offset[1] === null ? null : [offset[0], offset[1]]),
                "offset",
            ),
        )

        const [rotationValid, rotationInvalid] = scope.branch(
            scope.pureLambda(evaluator.evaluate(rotationParam), (rotation) => (rotation === null ? null : rotation * (Math.PI / 180)), "rotation"),
        )
        const [distanceValid, distanceInvalid] = scope.branch(evaluator.evaluate(distanceParam))

        const {completeMeshData} = scope.node(GetMeshDecal, {
            sceneManager,
            computeDummyReifiedMeshData: computeDummyReifiedMeshData && !isEvaluatedTemplateNode(this, activeNodeSet),
            inputCompleteMeshData: scope.get(meshObjectData, "completeMeshData"),
            offset: offsetValid,
            rotation: rotationValid,
            distance: distanceValid,
            size,
        })

        const parentMesh = scope.pureLambda(
            meshObjectData,
            (meshObjectData) => {
                return meshObjectData.displayList.find(SceneNodes.Mesh.is) ?? null
            },
            "parentMesh",
        )

        const invalidator = scope.phi(meshObjectDataInvalid, offsetInvalid, rotationInvalid, distanceInvalid)

        this.setupObject(
            scope,
            context,
            "Mesh",
            scope.get(scope.get(completeMeshData, "reified"), "bounds"),
            meshObjectData,
            (objectProps) => {
                const {materialData, preDisplayList} = setupMaterialAssignmentWithOverride(
                    scope.scope("material"),
                    context,
                    scope,
                    objectProps.path,
                    this,
                    materialAssignment,
                    undefined,
                )
                const [materialAssignmentData, materialAssignmentDataInvalid] = scope.branch(materialData)

                const materialGraph = scope.phi(scope.get(materialAssignmentData, "materialGraph"), materialAssignmentDataInvalid)

                const [materialGraphValid, materialGraphInvalid] = scope.branch(materialGraph)
                const [transformedMaterialGraph, transformedMaterialGraphInvalid] = scope.branch(
                    scope.phi(
                        scope.node(TransformColorOverlay, {
                            material: materialGraphValid,
                            image: colorImageData,
                            size,
                            useAlpha: false,
                        }).outputMaterial,
                        materialGraphInvalid,
                    ),
                )

                const alphaMaskThreshold = getAlphaMaskThresholdFromDecalMaskType(maskType)

                const decalMaterialData = scope.phi(
                    scope.pureLambda(
                        scope.tuple(transformedMaterialGraph, maskImageData, colorImageData, size, invertMask, alphaMaskThreshold, materialData),
                        ([transformedMaterialGraph, maskImageData, colorImageData, size, invertMask, alphaMaskThreshold, materialData]) => {
                            const materialDataWithMask: IMaterialData = {
                                name: transformedMaterialGraph.parameters.name ?? `Material ${transformedMaterialGraph.uniqueId}`,
                                materialGraph: transformDecalMask(transformedMaterialGraph, {
                                    maskImage: maskImageData ? maskImageData.imageNode : undefined,
                                    colorOverlayImage: colorImageData ? colorImageData.imageNode : undefined,
                                    widthCm: size[0],
                                    heightCm: size[1],
                                    invert: invertMask,
                                }),
                                side: "front",
                                alphaMaskThreshold,
                                realtimeSettings: materialData?.realtimeSettings,
                            }
                            return materialDataWithMask
                        },
                        "materialMapValid",
                    ),
                    transformedMaterialGraphInvalid,
                )

                const materialMap = scope.pureLambda(decalMaterialData, (materialData) => new Map([[0, materialData]]), "materialMap")

                return {
                    displayList: scope.struct<SceneNodes.Mesh>("Mesh", {
                        type: "Mesh",
                        ...objectProps,
                        meshRenderSettings: scope.struct<MeshRenderSettings>("MeshRenderSettings", {}),
                        completeMeshData,
                        materialMap,
                        visibleDirectly: scope.get(meshObjectData, "visibleDirectly"),
                        visibleInReflections: scope.get(meshObjectData, "visibleInReflections"),
                        visibleInRefractions: scope.get(meshObjectData, "visibleInRefractions"),
                        castRealtimeShadows: scope.get(meshObjectData, "castRealtimeShadows"),
                        receiveRealtimeShadows: scope.get(meshObjectData, "receiveRealtimeShadows"),
                        isDecal: true,
                        parent: parentMesh,
                        isProcedural: true,
                    }),
                    preDisplayList,
                }
            },
            invalidator,
        )

        return visitNone(this.parameters)
    }
}

function getAlphaMaskThresholdFromDecalMaskType(decalMaskType: DecalMaskType) {
    switch (decalMaskType) {
        case "binary":
            return 0.5
        case "opacity":
            return 0.0
        default:
            throw Error("Unrecognized decal mask type.")
    }
}

function computeConstrainedSizeFromImageData(inputSize: [number | null, number | null], imageData: ImageGenerator | null, logger: CmLogger): [number, number] {
    if (inputSize[0] !== null && inputSize[1] !== null) {
        return [inputSize[0], inputSize[1]]
    } else if (inputSize[0] === null && inputSize[1] === null) {
        return [10, 10]
    } else {
        const metadata = imageData?.metadata

        if (!metadata) {
            logger.error("Image metadata not available")
            return [10, 10]
        }

        const {width, height, legacyId} = metadata

        if (width === undefined || width === null || height === undefined || height === null) {
            logger.error(`DataObject ${legacyId ?? " with unknown id "} width/height not available`)
            return [10, 10]
        } else if (inputSize[0] === null) {
            if (inputSize[1] === null) {
                logger.error(`DataObject ${legacyId ?? " with unknown id "} input size 0/1 not available`)
                return [10, 10]
            }
            return [(inputSize[1] * width) / height, inputSize[1]]
        } else {
            return [inputSize[0], (inputSize[0] * height) / width]
        }
    }
}

function setUpMask(data: DecalMaskData) {
    const setUpMaskTextureNode = (image: ImageNodeGenerator) => {
        const uv = materialConnection(new UvMap({parameters: {inputs: {}, settings: {uvMapIndex: 0}}}), "uv")
        const normalizedUVs = materialConnection(
            new Mapping({parameters: {inputs: {vector: uv}, settings: {vectorType: "TEXTURE", scale: {x: data.widthCm, y: data.heightCm, z: 1}}}}),
            "vector",
        )
        return image.generator({uv: normalizedUVs, extension: "EXTEND", interpolation: "Linear", projection: "FLAT", size: [data.widthCm, data.heightCm]})
    }

    let maskValue: InputMaterialConnection<"float"> | number
    if (data.maskImage) {
        const maskTex = setUpMaskTextureNode(data.maskImage)
        let maskLuminance: InputMaterialConnection<"color"> = materialConnection(
            new RgbToBw({
                parameters: {
                    inputs: {
                        color: maskTex.color,
                    },
                    settings: {},
                },
            }),
            "val",
        )
        if (data.invert) {
            maskLuminance = sub(1, maskLuminance)
        }
        maskValue = mul(maskLuminance, maskTex.alpha)
    } else {
        maskValue = 1.0
    }

    if (data.colorOverlayImage) {
        const overlayTex = setUpMaskTextureNode(data.colorOverlayImage)
        maskValue = mul(maskValue, overlayTex.alpha)
    }

    return maskValue
}

export function transformDecalMask(originalGraph: MaterialGraph, maskData: DecalMaskData): MaterialGraph {
    const graph = originalGraph.clone({cloneSubNode: () => true})
    for (const node of graph.nodes) {
        if (node instanceof BsdfPrincipled) {
            const alpha = node.parameters.inputs.alpha ?? node.parameters.settings.alpha ?? 1
            const mask = setUpMask(maskData)
            const multiplied = mul(alpha, mask)
            node.parameters.inputs.alpha = multiplied
        }
    }
    return graph
}
